import React, { useRef, useEffect, useState } from 'react';

import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import DataTable, {
  DataTableBody,
  DataTableHeader,
  DataTableCell,
  DataTableRow,
  DataTableHead
} from '@amzn/meridian/data-table';
import DateInputField from '@amzn/meridian/date-input-field';
import Text from '@amzn/meridian/text';
import { Button, DatePicker, Input, Row, Toggle } from '@amzn/meridian';
import InputMask from '@amzn/meridian/input/mask';
import { ASSUMPTION } from '../../utils/constants';
import { chainWalk, formatDateForGraph, headcountPlanToIndirectHeadcountAllocations } from '../../utils/helpers';

const DynamicShiftPlanBetaTable = (props) => {
  const PackageProfileTable = (props) => {
    const packageProfileTableRef = useRef(null);
    const packageProfileValuesRefs = useRef({});
    const shiftPlanInboundPackageProfile = chainWalk(
      () => props.shiftPlanDynamic['input']['inboundPackageProfile'],
      {}
    );
    const inboundPackageProfileByInterval = chainWalk(
      () => props.assumptions['inboundPackageProfile']['inboundPackageProfileByInterval'],
      chainWalk(() => shiftPlanInboundPackageProfile['inboundPackageProfileByInterval'], {})
    );
    const shiftPlanOutboundPackageProfile = chainWalk(
      () => props.shiftPlanDynamic['input']['outboundPackageProfile'],
      {}
    );
    const outboundPackageProfileByInterval = chainWalk(
      () => props.assumptions['outboundPackageProfile']['outboundPackageProfileByInterval'],
      chainWalk(() => shiftPlanOutboundPackageProfile['outboundPackageProfileByInterval'], {})
    );

    const [isUseDefault, setIsUseDefault] = useState(
      !('inboundPackageProfile' in props.assumptions || 'outboundPackageProfile' in props.assumptions)
    );

    useEffect(() => {
      const container = packageProfileTableRef.current;
      let isInsideComponent = false;

      const handleFocusIn = (e) => {
        if (!isInsideComponent) {
          isInsideComponent = true;
        }
      };

      const handleFocusOut = (e) => {
        // Check if the new focused element is outside the component
        if (!container.contains(e.relatedTarget)) {
          isInsideComponent = false;
          setParentAssumptionsData();
        }
      };

      container.addEventListener('focusin', handleFocusIn);
      container.addEventListener('focusout', handleFocusOut);

      return () => {
        container.removeEventListener('focusin', handleFocusIn);
        container.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const setInputRef = (id, element) => {
      if (element !== null) {
        packageProfileValuesRefs.current[id] = element;
      }
    };

    const times = [...new Set([...Object.keys(inboundPackageProfileByInterval)])];
    const headerRow = (
      <DataTableRow>
        <DataTableHeader />
        {times.map((time) => (
          <DataTableHeader textAlign="end">{time.split('T')[1]}</DataTableHeader>
        ))}
      </DataTableRow>
    );

    function reset() {
      Object.entries(shiftPlanInboundPackageProfile['inboundPackageProfileByInterval']).forEach(
        ([time, valueObject]) => {
          const volumeValue =
            valueObject['packageVolumeByProcessFlow'][props.selectedCycle]['packageVolumeBySize']['MEDIUM'];
          const key = ['inboundPackageProfile', 'inboundPackageProfileByInterval', time, props.selectedCycle].join('^');
          if (key in packageProfileValuesRefs.current) {
            const element = packageProfileValuesRefs.current[key];
            element.value = parseInt(volumeValue);
          }
        }
      );
      setParentAssumptionsData();
    }

    function setParentAssumptionsData() {
      // The isUseDefault component is responsible for toggling whether or not
      // this assumption exists.
      // If we remove this check, we enter into an infinite render loop.
      props.setAssumptions((prev) => {
        if ('inboundPackageProfile' in prev || 'outboundPackageProfile' in prev) {
          if (!('inboundPackageProfile' in prev)) {
            prev['inboundPackageProfile'] = {
              ...shiftPlanInboundPackageProfile,
              overrideBasis: {
                reason: ASSUMPTION,
                comment: ''
              }
            };
          }
          if (!('outboundPackageProfile' in prev)) {
            prev['outboundPackageProfile'] = {
              ...shiftPlanOutboundPackageProfile,
              overrideBasis: {
                reason: ASSUMPTION,
                comment: ''
              }
            };
          }

          const prevInboundPackageProfile = JSON.stringify(prev['inboundPackageProfile']);
          const updatedInboundPackageProfile = JSON.parse(prevInboundPackageProfile);

          Object.entries(packageProfileValuesRefs.current).forEach(([key, element]) => {
            const [profileKey, intervalKey, time, cycle] = key.split('^');
            updatedInboundPackageProfile[intervalKey][time]['packageVolumeByProcessFlow'][cycle]['packageVolumeBySize'][
              'MEDIUM'
            ] = parseInt(element.value);
          });
          const newAssumptions = {
            ...prev,
            inboundPackageProfile: { ...updatedInboundPackageProfile }
          };

          return newAssumptions;
        } else {
          return prev;
        }
      });
    }

    const bodyRows = Object.entries({
      'Available Volume': inboundPackageProfileByInterval
    }).map(([profileType, profileData]) => (
      <DataTableRow highlightOnHover={true}>
        <DataTableCell>{profileType}</DataTableCell>
        {times.map((time, index, arr) => {
          const profileKey = 'inboundPackageProfile';
          const intervalKey = 'inboundPackageProfileByInterval';
          const inputElementId = [profileKey, intervalKey, time, props.selectedCycle].join('^');
          const initialValue = chainWalk(
            () => profileData[time]['packageVolumeByProcessFlow'][props.selectedCycle]['packageVolumeBySize']['MEDIUM'],
            '-'
          );
          const [value, setValue] = useState(initialValue);

          return (
            <DataTableCell textAlign="end">
              {props.isEditMode &&
              (time >= props.stationLocalStablePlanTime ||
                (time < props.stationLocalStablePlanTime &&
                  index === 0 &&
                  arr.length >= 2 &&
                  arr[index + 1] >= props.stationLocalStablePlanTime)) ? (
                <Row alignmentHorizontal="end">
                  <Input
                    id={inputElementId}
                    disabled={isUseDefault}
                    ref={(el) => setInputRef(inputElementId, el)}
                    width={100}
                    value={value}
                    size="small"
                    onChange={setValue}
                  />
                </Row>
              ) : (
                value
              )}
            </DataTableCell>
          );
        })}
      </DataTableRow>
    ));

    // Get packageProjectionInput from input or assumptions
    const shiftPlanPackageProjection = chainWalk(() => props.shiftPlanDynamic['input']['packageProjectionInput'], null);
    const packageProjectionInput = chainWalk(
      () => props.assumptions['packageProjectionInput'],
      shiftPlanPackageProjection
    );

    const currentCycleProjection = chainWalk(
      () =>
        packageProjectionInput?.packageProjectionList?.find(
          (projection) => projection.applicability.identifier === props.selectedCycle
        ),
      null
    );

    const controlledRolloverVolumeFromData = chainWalk(() => currentCycleProjection?.volume?.plannedRolloverVolume, 0);
    const [controlledRolloverVolume, setControlledRolloverVolume] = useState(controlledRolloverVolumeFromData);

    // Only update when cycle changes, using a ref to track the last cycle
    const lastCycleRef = useRef(props.selectedCycle);
    if (lastCycleRef.current !== props.selectedCycle) {
      setControlledRolloverVolume(controlledRolloverVolumeFromData);
      lastCycleRef.current = props.selectedCycle;
    }

    function onBlurControlledRolloverVolume(event) {
      props.setAssumptions((prev) => {
        const defaultProjection = {
          overrideBasis: {
            reason: ASSUMPTION,
            comment: ''
          },
          packageProjectionList: [
            {
              applicability: {
                identifier: props.selectedCycle,
                timeWindow: null,
                type: null
              },
              cycleVolumeDistributionOverride: null,
              volume: {
                lastDayRolloverVolume: 0,
                plannedRolloverVolume: parseInt(controlledRolloverVolume) || 0,
                plannedVolume: 0
              }
            }
          ]
        };

        if (!('packageProjectionInput' in prev)) {
          prev['packageProjectionInput'] = shiftPlanPackageProjection || defaultProjection;
        }

        const updatedProjectionList = [...(prev.packageProjectionInput.packageProjectionList || [])];
        const cycleIndex = updatedProjectionList.findIndex(
          (projection) => projection.applicability.identifier === props.selectedCycle
        );

        if (cycleIndex >= 0) {
          updatedProjectionList[cycleIndex] = {
            ...updatedProjectionList[cycleIndex],
            volume: {
              ...updatedProjectionList[cycleIndex].volume,
              plannedRolloverVolume: parseInt(controlledRolloverVolume) || 0
            }
          };
        } else {
          updatedProjectionList.push(defaultProjection.packageProjectionList[0]);
        }

        return {
          ...prev,
          packageProjectionInput: {
            ...prev.packageProjectionInput,
            packageProjectionList: updatedProjectionList,
            overrideBasis: {
              reason: ASSUMPTION,
              comment: ''
            }
          }
        };
      });
    }
    //                 'packageVolumeByProcessFlow'
    //               ],
    //               [props.selectedCycle]: {
    //                 ...prev.outboundPackageProfile.outboundPackageProfileByInterval[controlledRolloverInterval][
    //                   'packageVolumeByProcessFlow'
    //                 ][props.selectedCycle],
    //                 packageVolumeBySize: {
    //                   ...prev.outboundPackageProfile.outboundPackageProfileByInterval[controlledRolloverInterval][
    //                     'packageVolumeByProcessFlow'
    //                   ][props.selectedCycle]['packageVolumeBySize'],
    //                   MEDIUM: totalArrivalVolume - controlledRolloverVolume
    //                 }
    //               }
    //             }
    //           }
    //         }
    //       }
    //     };
    //   });
    // }

    return (
      <Column>
        <Column ref={packageProfileTableRef}>
          <Box type="fill" spacingInset="300">
            <Row>
              <Text type="h300">
                Arrived Volume{' '}
                {chainWalk(
                  () => props.shiftPlanDynamic['input']['inboundPackageProfile']['overrideBasis']['reason'],
                  false
                ) && '(Overridden)'}
              </Text>
              {props.isEditMode && (
                <Row>
                  <Button
                    size="small"
                    type="secondary"
                    disabled={isUseDefault}
                    onClick={() => {
                      reset();
                    }}>
                    Reset
                  </Button>
                  <Toggle
                    checked={isUseDefault}
                    onChange={(checked) => {
                      setIsUseDefault(checked);

                      if (checked) {
                        props.setAssumptions((prev) => {
                          const currAssumptions = { ...prev };
                          delete currAssumptions['inboundPackageProfile'];
                          delete currAssumptions['outboundPackageProfile'];
                          return currAssumptions;
                        });
                      } else {
                        props.setAssumptions((prev) => ({
                          ...prev,
                          inboundPackageProfile: {
                            ...shiftPlanInboundPackageProfile,
                            overrideBasis: {
                              reason: ASSUMPTION,
                              comment: ''
                            }
                          },
                          outboundPackageProfile: {
                            ...shiftPlanOutboundPackageProfile,
                            overrideBasis: {
                              reason: ASSUMPTION,
                              comment: ''
                            }
                          }
                        }));
                      }
                    }}>
                    Use default
                  </Toggle>
                </Row>
              )}
            </Row>
          </Box>
          <DataTable headerRows={1} headerColumns={1} stickyHeaderColumn={true}>
            <DataTableHead>{headerRow}</DataTableHead>
            <DataTableBody>{bodyRows}</DataTableBody>
          </DataTable>
        </Column>
        <br />
        {props.isEditMode ? (
          <Input
            disabled={isUseDefault}
            size="xlarge"
            width={300}
            type="number"
            value={controlledRolloverVolume}
            onChange={setControlledRolloverVolume}
            onBlur={onBlurControlledRolloverVolume}
            label={'Controlled Rollover Volume'}
          />
        ) : (
          <Text>Controlled Rollover Volume: {controlledRolloverVolume}</Text>
        )}
      </Column>
    );
  };

  const LaborAvailabilitiesTable = (props) => {
    const availabilityTableRef = useRef(null);
    const groupAvailabilityRefs = useRef({});
    const shiftPlanLabourAvailability = chainWalk(() => props.shiftPlanDynamic['input']['labourAvailability'], {});
    const shiftGroupAvailabilities = chainWalk(
      () => props.assumptions['labourAvailability']['shiftGroupAvailabilities'],
      chainWalk(() => shiftPlanLabourAvailability['shiftGroupAvailabilities'], [])
    );

    const [isUseDefault, setIsUseDefault] = useState(!('labourAvailability' in props.assumptions));

    function reset() {
      shiftPlanLabourAvailability['shiftGroupAvailabilities'].forEach((groupAvailability) => {
        const identifier = groupAvailability['applicability']['identifier'];
        const headCount = groupAvailability['labourAvailabilityHeadCount']['expectedHeadcount'];
        if (identifier in groupAvailabilityRefs.current) {
          const element = groupAvailabilityRefs.current[identifier];
          element.value = headCount;
        }
      });
      setParentAssumptionsData();
    }

    useEffect(() => {
      const container = availabilityTableRef.current;
      let isInsideComponent = false;

      const handleFocusIn = (e) => {
        if (!isInsideComponent) {
          isInsideComponent = true;
        }
      };

      const handleFocusOut = (e) => {
        // Check if the new focused element is outside the component
        if (!container.contains(e.relatedTarget)) {
          isInsideComponent = false;
          setParentAssumptionsData();
        }
      };

      container.addEventListener('focusin', handleFocusIn);
      container.addEventListener('focusout', handleFocusOut);

      return () => {
        container.removeEventListener('focusin', handleFocusIn);
        container.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const setInputRef = (id, element) => {
      if (element !== null) {
        groupAvailabilityRefs.current[id] = element;
      }
    };

    function setParentAssumptionsData() {
      // The isUseDefault component is responsible for toggling whether or not
      // this assumption exists.
      // If we remove this check, we enter into an infinite render loop.
      props.setAssumptions((prev) => {
        if ('labourAvailability' in prev) {
          const newAssumptions = {
            ...prev,
            labourAvailability: {
              ...prev['labourAvailability'],
              shiftGroupAvailabilities: prev['labourAvailability']['shiftGroupAvailabilities'].map(
                (prevShiftGroupAvailability) => {
                  if (prevShiftGroupAvailability['applicability']['identifier'] in groupAvailabilityRefs.current) {
                    const element =
                      groupAvailabilityRefs.current[prevShiftGroupAvailability['applicability']['identifier']];
                    const value = element ? parseInt(element.value) : NaN;
                    return {
                      ...prevShiftGroupAvailability,
                      labourAvailabilityHeadCount: {
                        ...prevShiftGroupAvailability['labourAvailabilityHeadCount'],
                        expectedHeadcount: value
                      }
                    };
                  }
                  return prevShiftGroupAvailability;
                }
              )
            }
          };

          return newAssumptions;
        } else {
          return prev;
        }
      });
    }

    const headerRow = (
      <DataTableHead>
        <DataTableRow>
          <DataTableHeader colSpan={2}>Applicability</DataTableHeader>
          <DataTableHeader>Headcount</DataTableHeader>
        </DataTableRow>
        <DataTableRow>
          <DataTableHeader>Start</DataTableHeader>
          <DataTableHeader>End</DataTableHeader>
          <DataTableHeader />
        </DataTableRow>
      </DataTableHead>
    );

    const bodyRows = shiftGroupAvailabilities.map((shiftGroupAvailability) => {
      const [value, setValue] = useState(shiftGroupAvailability['labourAvailabilityHeadCount']['expectedHeadcount']);
      const startTime = shiftGroupAvailability['applicability']['timeWindow']['start'].slice(0, -3);
      const endTime = shiftGroupAvailability['applicability']['timeWindow']['end'].slice(0, -3);
      const applicabilityIdentifier = shiftGroupAvailability['applicability']['identifier'];

      return (
        <DataTableRow highlightOnHover={true}>
          <DataTableCell>{startTime} </DataTableCell>
          <DataTableCell>{endTime}</DataTableCell>
          <DataTableCell textAlign="end">
            {props.isEditMode &&
            shiftGroupAvailability['applicability']['timeWindow']['end'] >= props.stationLocalStablePlanTime ? (
              <Row alignmentHorizontal="end">
                <Input
                  id={applicabilityIdentifier}
                  disabled={isUseDefault}
                  ref={(el) => setInputRef(applicabilityIdentifier, el)}
                  width={100}
                  value={value}
                  size="small"
                  onChange={setValue}
                />
              </Row>
            ) : (
              value
            )}
          </DataTableCell>
        </DataTableRow>
      );
    });

    return (
      <Column ref={availabilityTableRef}>
        <Box type="fill" spacingInset="300">
          <Row>
            <Text type="h300">
              Labor Availabilities{' '}
              {chainWalk(
                () => props.shiftPlanDynamic['input']['labourAvailability']['overrideBasis']['reason'],
                false
              ) && '(Overridden)'}
            </Text>
            {props.isEditMode && (
              <Row>
                <Button
                  size="small"
                  type="secondary"
                  disabled={isUseDefault}
                  onClick={() => {
                    reset();
                  }}>
                  Reset
                </Button>
                <Toggle
                  checked={isUseDefault}
                  onChange={(checked) => {
                    setIsUseDefault(checked);

                    if (checked) {
                      props.setAssumptions((prev) => {
                        delete prev['labourAvailability'];
                        return prev;
                      });
                    } else {
                      props.setAssumptions((prev) => ({
                        ...prev,
                        labourAvailability: {
                          ...shiftPlanLabourAvailability,
                          overrideBasis: {
                            reason: ASSUMPTION,
                            comment: ''
                          }
                        }
                      }));
                    }
                  }}>
                  Use default
                </Toggle>
              </Row>
            )}
          </Row>
        </Box>
        <DataTable headerRows={1} width="50%">
          {headerRow}
          <DataTableBody>{bodyRows}</DataTableBody>
        </DataTable>
      </Column>
    );
  };

  const RatesTable = (props) => {
    const ratesTableRef = useRef(null);
    const ratesValuesRefs = useRef({});
    const shiftGroupAvailabilities = chainWalk(
      () => props.shiftPlanDynamic['input']['labourAvailability']['shiftGroupAvailabilities'],
      []
    );
    const shiftPlanProductivities = chainWalk(
      () =>
        props.shiftPlanDynamic['input']['productivityByLaborGroupAndCycleInput']['productivityByLaborGroupAndCycleMap'][
          props.selectedCycle
        ],
      {}
    );
    const productivities = chainWalk(
      () =>
        props.assumptions['productivityByLaborGroupAndCycleInput']['productivityByLaborGroupAndCycleMap'][
          props.selectedCycle
        ],
      shiftPlanProductivities
    );

    const [isUseDefault, setIsUseDefault] = useState(!('productivityByLaborGroupAndCycleInput' in props.assumptions));
    useEffect(() => {
      const container = ratesTableRef.current;
      let isInsideComponent = false;

      const handleFocusIn = (e) => {
        if (!isInsideComponent) {
          isInsideComponent = true;
        }
      };

      const handleFocusOut = (e) => {
        // Check if the new focused element is outside the component
        if (!container.contains(e.relatedTarget)) {
          isInsideComponent = false;
          setParentAssumptionsData();
        }
      };

      container.addEventListener('focusin', handleFocusIn);
      container.addEventListener('focusout', handleFocusOut);

      return () => {
        container.removeEventListener('focusin', handleFocusIn);
        container.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const setInputRef = (id, element) => {
      if (element !== null) {
        ratesValuesRefs.current[id] = element;
      }
    };

    function reset() {
      Object.entries(shiftPlanProductivities).forEach(([shiftGroupIdentifier, identifiersMap]) => {
        Object.entries(identifiersMap).forEach(([identifier, valueObject]) => {
          const rateValue = valueObject['numOfPackagesBySize']['MEDIUM'];
          const key = [shiftGroupIdentifier, identifier].join('^');
          if (key in ratesValuesRefs.current) {
            const element = ratesValuesRefs.current[key];
            element.value = Math.round(rateValue);
          }
        });
      });
      setParentAssumptionsData();
    }

    const columnTimes = [];
    const shiftGroupIds = [];

    shiftGroupAvailabilities.flatMap((shiftGroupAvailability) => {
      columnTimes.push({
        start: shiftGroupAvailability['applicability']['timeWindow']['start'],
        end: shiftGroupAvailability['applicability']['timeWindow']['end']
      });
      shiftGroupIds.push(shiftGroupAvailability['applicability']['identifier']);
    });

    const processPaths =
      shiftGroupIds.length > 0 && productivities[shiftGroupIds[0]]
        ? Object.entries(productivities[shiftGroupIds[0]]).map(([identifier, _]) => {
            return identifier;
          })
        : [];

    function setParentAssumptionsData() {
      // The isUseDefault component is responsible for toggling whether or not
      // this assumption exists.
      // If we remove this check, we enter into an infinite render loop.
      props.setAssumptions((prev) => {
        if ('productivityByLaborGroupAndCycleInput' in prev) {
          const prevProductivityByLaborGroupMap = JSON.stringify(
            prev['productivityByLaborGroupAndCycleInput']['productivityByLaborGroupAndCycleMap'][props.selectedCycle]
          );
          const updatedProductivitiesByLaborGroup = JSON.parse(prevProductivityByLaborGroupMap);

          Object.entries(ratesValuesRefs.current).forEach(([key, element]) => {
            const [shiftGroupIdentifier, identifier] = key.split('^');
            updatedProductivitiesByLaborGroup[shiftGroupIdentifier][identifier]['numOfPackagesBySize']['MEDIUM'] =
              parseInt(element.value);
          });

          const newAssumptions = {
            ...prev,
            productivityByLaborGroupAndCycleInput: {
              ...prev['productivityByLaborGroupAndCycleInput'],
              productivityByLaborGroupAndCycleMap: {
                ...prev['productivityByLaborGroupAndCycleInput']['productivityByLaborGroupAndCycleMap'],
                [props.selectedCycle]: {
                  ...updatedProductivitiesByLaborGroup
                }
              }
            }
          };
          return newAssumptions;
        } else {
          return prev;
        }
      });
    }

    const tableHeaderRow = (
      <DataTableRow>
        <DataTableHeader />
        {columnTimes.map((startEndTime) => {
          const label =
            startEndTime['start'].slice(0, -3).split('T')[1] + ' - ' + startEndTime['end'].slice(0, -3).split('T')[1];
          return <DataTableHeader textAlign="end">{label}</DataTableHeader>;
        })}
      </DataTableRow>
    );

    const tableBodyRows = processPaths.map((identifier) => {
      return (
        <DataTableRow highlightOnHover={true}>
          <DataTableCell>{identifier}</DataTableCell>
          {shiftGroupIds.map((shiftGroupIdentifier, index) => {
            const productivitiesGroup = productivities[shiftGroupIdentifier] || {};
            const rateValueIdentifier = [shiftGroupIdentifier, identifier].join('^');
            const [value, setValue] = useState(
              Math.round(chainWalk(() => productivitiesGroup[identifier]['numOfPackagesBySize']['MEDIUM'], 0))
            );

            return (
              <DataTableCell textAlign="end" size="large">
                {props.isEditMode && columnTimes[index]['end'] >= props.stationLocalStablePlanTime ? (
                  <Row alignmentHorizontal="end">
                    <Input
                      id={rateValueIdentifier}
                      disabled={isUseDefault}
                      ref={(el) => setInputRef(rateValueIdentifier, el)}
                      width={100}
                      value={value}
                      size="small"
                      onChange={setValue}
                    />
                  </Row>
                ) : (
                  value
                )}
              </DataTableCell>
            );
          })}
        </DataTableRow>
      );
    });

    return (
      <Column ref={ratesTableRef}>
        <Box type="fill" spacingInset="300">
          <Row>
            <Text type="h300">
              Rates{' '}
              {chainWalk(
                () =>
                  props.shiftPlanDynamic['input']['productivityByLaborGroupAndCycleInput']['overrideBasis']['reason'],
                false
              ) && '(Overridden)'}
            </Text>
            {props.isEditMode && (
              <Row>
                <Button
                  size="small"
                  type="secondary"
                  disabled={isUseDefault}
                  onClick={() => {
                    reset();
                  }}>
                  Reset
                </Button>
                <Toggle
                  checked={isUseDefault}
                  onChange={(checked) => {
                    setIsUseDefault(checked);

                    if (checked) {
                      props.setAssumptions((prev) => {
                        const currAssumptions = { ...prev };
                        delete currAssumptions['productivityByLaborGroupAndCycleInput'];
                        return currAssumptions;
                      });
                    } else {
                      props.setAssumptions((prev) => ({
                        ...prev,
                        productivityByLaborGroupAndCycleInput: {
                          ...props.shiftPlanDynamic['input']['productivityByLaborGroupAndCycleInput'],
                          overrideBasis: {
                            reason: ASSUMPTION,
                            comment: ''
                          }
                        }
                      }));
                    }
                  }}>
                  Use default
                </Toggle>
              </Row>
            )}
          </Row>
        </Box>
        <DataTable stickyHeaderColumn={true} showColumnDividers={true}>
          <DataTableHead>{tableHeaderRow}</DataTableHead>
          <DataTableBody>{tableBodyRows}</DataTableBody>
        </DataTable>
      </Column>
    );
  };

  const MechanicalConstraintsTable = (props) => {
    const mechanicalConstraintsTableRef = useRef(null);
    const mechanicalConstraintsRefs = useRef({});
    const shiftPlanMechanicalConstraints = chainWalk(
      () => props.shiftPlanDynamic['input']['mechanicalConstraintsInput']['mechanicalConstraintsMap'],
      {}
    );
    const initialMechanicalConstraints = chainWalk(
      () => props.assumptions['mechanicalConstraintsInput']['mechanicalConstraintsMap'],
      shiftPlanMechanicalConstraints
    );

    const [isUseDefault, setIsUseDefault] = useState(!('mechanicalConstraintsInput' in props.assumptions));

    const [bufferAisles, setBufferAisles] = useState(initialMechanicalConstraints['BUFFER_AISLES']);
    const [adtaBufferAisles, setAdtaBufferAisles] = useState(initialMechanicalConstraints['ADTA_BUFFER_AISLES']);
    const [inductLines, setInductLines] = useState(initialMechanicalConstraints['INDUCT_LINES']);

    function reset() {
      setBufferAisles(shiftPlanMechanicalConstraints['BUFFER_AISLES']);
      setAdtaBufferAisles(shiftPlanMechanicalConstraints['ADTA_BUFFER_AISLES']);
      setInductLines(shiftPlanMechanicalConstraints['INDUCT_LINES']);
    }

    useEffect(() => {
      const container = mechanicalConstraintsTableRef.current;
      let isInsideComponent = false;

      const handleFocusIn = (e) => {
        if (!isInsideComponent) {
          isInsideComponent = true;
        }
      };

      const handleFocusOut = (e) => {
        // Check if the new focused element is outside the component
        if (!container.contains(e.relatedTarget)) {
          isInsideComponent = false;
          setParentAssumptionsData();
        }
      };

      container.addEventListener('focusin', handleFocusIn);
      container.addEventListener('focusout', handleFocusOut);

      return () => {
        container.removeEventListener('focusin', handleFocusIn);
        container.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const setInputRef = (id, element) => {
      if (element !== null) {
        mechanicalConstraintsRefs.current[id] = element;
      }
    };

    function setParentAssumptionsData() {
      // The isUseDefault component is responsible for toggling whether or not
      // this assumption exists.
      // If we remove this check, we enter into an infinite render loop.
      props.setAssumptions((prev) => {
        if ('mechanicalConstraintsInput' in prev) {
          return {
            ...prev,
            mechanicalConstraintsInput: {
              mechanicalConstraintsMap: {
                BUFFER_AISLES: parseInt(mechanicalConstraintsRefs.current['bufferAisles'].value),
                ADTA_BUFFER_AISLES: parseInt(mechanicalConstraintsRefs.current['adtaBufferAisles'].value),
                INDUCT_LINES: parseInt(mechanicalConstraintsRefs.current['inductLines'].value)
              },
              overrideBasis: {
                reason: ASSUMPTION,
                comment: ''
              }
            }
          };
        } else {
          return prev;
        }
      });
    }

    const headerRow = (
      <DataTableHead>
        <DataTableRow>
          <DataTableHeader textAlign="end">non-ADTA Buffer Racks</DataTableHeader>
          <DataTableHeader textAlign="end">ADTA Buffer Racks</DataTableHeader>
          <DataTableHeader textAlign="end">Max Induct Lines</DataTableHeader>
        </DataTableRow>
      </DataTableHead>
    );

    const bodyRows = (
      <DataTableRow highlightOnHover={true}>
        <DataTableCell textAlign="end">
          {props.isEditMode ? (
            <Row alignmentHorizontal="end">
              <Input
                id="bufferAisles"
                disabled={isUseDefault}
                ref={(el) => setInputRef('bufferAisles', el)}
                width={100}
                value={bufferAisles}
                size="small"
                onChange={setBufferAisles}
              />
            </Row>
          ) : (
            bufferAisles
          )}
        </DataTableCell>
        <DataTableCell textAlign="end">
          {props.isEditMode ? (
            <Row alignmentHorizontal="end">
              <Input
                id="adtaBufferAisles"
                disabled={isUseDefault}
                ref={(el) => setInputRef('adtaBufferAisles', el)}
                width={100}
                value={adtaBufferAisles}
                size="small"
                onChange={setAdtaBufferAisles}
              />
            </Row>
          ) : (
            adtaBufferAisles
          )}
        </DataTableCell>
        <DataTableCell textAlign="end">
          {props.isEditMode ? (
            <Row alignmentHorizontal="end">
              <Input
                id="inductLines"
                disabled={isUseDefault}
                ref={(el) => setInputRef('inductLines', el)}
                width={100}
                value={inductLines}
                size="small"
                onChange={setInductLines}
              />
            </Row>
          ) : (
            inductLines
          )}
        </DataTableCell>
      </DataTableRow>
    );

    return (
      <Column ref={mechanicalConstraintsTableRef}>
        <Box type="fill" spacingInset="300">
          <Row>
            <Text type="h300">
              Mechanical Constraints{' '}
              {chainWalk(
                () => props.shiftPlanDynamic['input']['mechanicalConstraintsInput']['overrideBasis']['reason'],
                false
              ) && '(Overridden)'}
            </Text>
            {props.isEditMode && (
              <Row>
                <Button
                  size="small"
                  type="secondary"
                  disabled={isUseDefault}
                  onClick={() => {
                    reset();
                  }}>
                  Reset
                </Button>
                <Toggle
                  checked={isUseDefault}
                  onChange={(checked) => {
                    setIsUseDefault(checked);

                    if (checked) {
                      props.setAssumptions((prev) => {
                        delete prev['mechanicalConstraintsInput'];
                        return prev;
                      });
                      // TODO: In the future, we'll probably want this from
                      // shiftPlanSuggestedInputs.
                    } else {
                      props.setAssumptions((prev) => ({
                        ...prev,
                        mechanicalConstraintsInput: {
                          mechanicalConstraintsMap: {
                            BUFFER_AISLES: parseInt(mechanicalConstraintsRefs.current['bufferAisles'].value),
                            ADTA_BUFFER_AISLES: parseInt(mechanicalConstraintsRefs.current['adtaBufferAisles'].value),
                            INDUCT_LINES: parseInt(mechanicalConstraintsRefs.current['inductLines'].value)
                          },
                          overrideBasis: {
                            reason: ASSUMPTION,
                            comment: ''
                          }
                        }
                      }));
                    }
                  }}>
                  Use default
                </Toggle>
              </Row>
            )}
          </Row>
        </Box>
        <DataTable headerRows={1} width="35%">
          {headerRow}
          <DataTableBody>{bodyRows}</DataTableBody>
        </DataTable>
      </Column>
    );
  };

  const HeadcountTable = (props) => {
    const headcountTableRef = useRef(null);
    const headcountValuesRefs = useRef({});

    // Get output headcount plan from shiftPlan (for reset/cancel purposes)
    const shiftPlanOutputHeadcountPlan = chainWalk(
      () => props.shiftPlanDynamic['output']['headCountPlan']['headCountCycles'],
      []
    );
    // Get indirect headcount allocations
    const indirectHeadCountAllocations = chainWalk(
      () => props.assumptions['indirectHeadCountAllocationsListInput']['indirectHeadCountAllocationList'],
      headcountPlanToIndirectHeadcountAllocations(shiftPlanOutputHeadcountPlan)
    ).filter((item) => item.cycleName === props.selectedCycle);

    const [isUseDefault, setIsUseDefault] = useState(!('indirectHeadCountAllocationsListInput' in props.assumptions));

    // Get unique time windows
    const timeWindows = [...new Set(indirectHeadCountAllocations.map((item) => `${item.startTime}|${item.endTime}`))]
      .map((timeStr) => {
        const [startTime, endTime] = timeStr.split('|');
        return { startTime, endTime };
      })
      .sort((a, b) => a.startTime.localeCompare(b.startTime));

    // Get list of labor processes
    const laborProcesses = [...new Set(indirectHeadCountAllocations.map((item) => item.laborProcess))].sort();

    useEffect(() => {
      const container = headcountTableRef.current;
      let isInsideComponent = false;

      const handleFocusIn = (e) => {
        if (!isInsideComponent) {
          isInsideComponent = true;
        }
      };

      const handleFocusOut = (e) => {
        // Check if the new focused element is outside the component
        if (!container.contains(e.relatedTarget)) {
          isInsideComponent = false;
          setParentAssumptionsData();
        }
      };

      container.addEventListener('focusin', handleFocusIn);
      container.addEventListener('focusout', handleFocusOut);

      return () => {
        container.removeEventListener('focusin', handleFocusIn);
        container.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const setInputRef = (id, element) => {
      if (element !== null) {
        headcountValuesRefs.current[id] = element;
      }
    };

    function reset() {
      const indirectHeadcountAllocations = headcountPlanToIndirectHeadcountAllocations(shiftPlanOutputHeadcountPlan);
      Object.entries(headcountValuesRefs.current).forEach(([key, element]) => {
        const [laborProcess, startTime, endTime] = key.split('^');

        // Find allocation entry
        const hcAllocationEntry = indirectHeadcountAllocations.find(
          (item) =>
            item.laborProcess === laborProcess &&
            item.cycleName === props.selectedCycle &&
            item.startTime === startTime &&
            item.endTime === endTime
        );

        if (hcAllocationEntry) {
          element.value = hcAllocationEntry.assignedHeadCount;
        }
      });
      setParentAssumptionsData();
    }

    function setParentAssumptionsData() {
      // The isUseDefault component is responsible for toggling whether or not
      // this assumption exists.
      // If we remove this check, we enter into an infinite render loop.
      props.setAssumptions((prev) => {
        if ('indirectHeadCountAllocationsListInput' in prev) {
          const prevIndirectHeadcountAllocations = JSON.stringify(
            prev['indirectHeadCountAllocationsListInput']['indirectHeadCountAllocationList']
          );
          const updatedIndirectHeadcountAllocations = JSON.parse(prevIndirectHeadcountAllocations);

          // Add updated values from the inputs
          Object.entries(headcountValuesRefs.current).forEach(([key, element]) => {
            const [laborProcess, startTime, endTime] = key.split('^');
            const value = !isNaN(parseInt(element.value)) ? parseInt(element.value) : null;

            // Find allocation entry
            const hcAllocationEntry = updatedIndirectHeadcountAllocations.find(
              (item) =>
                item.laborProcess === laborProcess &&
                item.cycleName === props.selectedCycle &&
                item.startTime === startTime &&
                item.endTime === endTime
            );

            if (hcAllocationEntry) {
              if (hcAllocationEntry.assignedHeadCount !== value) {
                hcAllocationEntry.assignedHeadCount = value;
                hcAllocationEntry.isOverriden = true;
              }
            }
          });

          const newAssumptions = {
            ...prev,
            indirectHeadCountAllocationsListInput: {
              ...prev['indirectHeadCountAllocationsListInput'],
              indirectHeadCountAllocationList: updatedIndirectHeadcountAllocations
            }
          };

          return newAssumptions;
        } else {
          return prev;
        }
      });
    }

    const tableHeaderRow = (
      <DataTableRow>
        <DataTableHeader />
        {timeWindows.map((timeWindow) => {
          const startTime = timeWindow.startTime.split('T')[1].substring(0, 5);
          const endTime = timeWindow.endTime.split('T')[1].substring(0, 5);
          const label = `${startTime} - ${endTime}`;
          return <DataTableHeader textAlign="end">{label}</DataTableHeader>;
        })}
      </DataTableRow>
    );

    const tableBodyRows = laborProcesses.map((laborProcess) => {
      return (
        <DataTableRow highlightOnHover={true}>
          {/* Changing 'Label' process display label to 'Induct' */}
          <DataTableCell>{laborProcess === 'Label' ? 'Induct' : laborProcess}</DataTableCell>
          {timeWindows.map((timeWindow) => {
            const headcountItem = indirectHeadCountAllocations.find(
              (item) =>
                item.laborProcess === laborProcess &&
                item.startTime === timeWindow.startTime &&
                item.endTime === timeWindow.endTime
            );

            const initialValue = headcountItem ? headcountItem.assignedHeadCount : null;
            const [value, setValue] = useState(initialValue ? initialValue : '-');
            const headcountId = [laborProcess, timeWindow.startTime, timeWindow.endTime].join('^');

            return (
              <DataTableCell textAlign="end" size="large">
                {props.isEditMode && timeWindow.endTime >= props.stationLocalStablePlanTime ? (
                  <Row alignmentHorizontal="end">
                    <Input
                      id={headcountId}
                      disabled={isUseDefault}
                      ref={(el) => setInputRef(headcountId, el)}
                      width={60}
                      value={value}
                      size="small"
                      onChange={setValue}
                    />
                  </Row>
                ) : (
                  value
                )}
              </DataTableCell>
            );
          })}
        </DataTableRow>
      );
    });

    return (
      <Column ref={headcountTableRef}>
        <Box type="fill" spacingInset="300">
          <Row>
            <Text type="h300">
              Headcount{' '}
              {chainWalk(
                () =>
                  props.shiftPlanDynamic['input']['indirectHeadCountAllocationsListInput']['overrideBasis']['reason'],
                false
              ) && '(Overridden)'}
            </Text>
            {props.isEditMode && (
              <Row>
                <Button
                  size="small"
                  type="secondary"
                  disabled={isUseDefault}
                  onClick={() => {
                    reset();
                  }}>
                  Reset
                </Button>
                <Toggle
                  checked={isUseDefault}
                  onChange={(checked) => {
                    setIsUseDefault(checked);

                    if (checked) {
                      props.setAssumptions((prev) => {
                        const currAssumptions = { ...prev };
                        delete currAssumptions['indirectHeadCountAllocationsListInput'];
                        return currAssumptions;
                      });
                    } else {
                      props.setAssumptions((prev) => ({
                        ...prev,
                        indirectHeadCountAllocationsListInput: {
                          indirectHeadCountAllocationList:
                            headcountPlanToIndirectHeadcountAllocations(shiftPlanOutputHeadcountPlan),
                          overrideBasis: {
                            reason: ASSUMPTION,
                            comment: ''
                          }
                        }
                      }));
                    }
                  }}>
                  Use default
                </Toggle>
              </Row>
            )}
          </Row>
        </Box>
        <DataTable stickyHeaderColumn={true} showColumnDividers={true}>
          <DataTableHead>{tableHeaderRow}</DataTableHead>
          <DataTableBody>{tableBodyRows}</DataTableBody>
        </DataTable>
      </Column>
    );
  };

  // Map of display name to corresponding plan names.
  // Keys should match LMOCS processes.
  const OPS_CLOCK_PROCESS_MAPPINGS = {
    Sort_0: ['Twilight'],
    Induct: ['Induct', 'Auto Diverter'],
    Stow: ['Stow', 'Pick to Buffer', 'ADTA Stow'],
    'Pick Stage': ['Pick', 'Pick & Stage - Pick'],
    Dispatch: ['Dispatch']
  };
  const OpsClockTable = (props) => {
    const opsClockTableRef = useRef(null);
    const opsClockValuesRefs = useRef({});
    const shiftPlanOpsClockMap = chainWalk(
      () => props.shiftPlanDynamic['input']['opsclockInput']['opsClockMap'][props.selectedCycle],
      []
    );
    const initialOpsClockMap = chainWalk(
      () => props.assumptions['opsclockInput']['opsClockMap'][props.selectedCycle],
      shiftPlanOpsClockMap
    );
    const processes = Object.keys(OPS_CLOCK_PROCESS_MAPPINGS);

    const [isUseDefault, setIsUseDefault] = useState(!('opsclockInput' in props.assumptions));

    useEffect(() => {
      const container = opsClockTableRef.current;
      let isInsideComponent = false;

      const handleFocusIn = (e) => {
        if (!isInsideComponent) {
          isInsideComponent = true;
        }
      };

      const handleFocusOut = (e) => {
        // If new focused element is cancel button, proceed
        const relTargetId = e?.relatedTarget?.id;
        if (relTargetId === 'btnCancel') {
          reset();
          return;
        }

        // Check if the new focused element is outside the component
        if (!container.contains(e.relatedTarget)) {
          isInsideComponent = false;
          const refsKeys = Object.keys(opsClockValuesRefs.current);
          // Times need to be validated first for focusing back reasons
          for (let i = 0; i < refsKeys.length; i++) {
            const [process, dateTimeKey] = refsKeys[i].split('^');
            const valueString = opsClockValuesRefs.current[refsKeys[i]].value;
            if (!dateTimeKey.endsWith('Time') || dateTimeKey.endsWith('DateTime')) continue;
            const [isValid, errorMessage] = isValidOpsClockTime(valueString, refsKeys[i]);
            if (!isValid) {
              e.preventDefault();
              opsClockValuesRefs.current[refsKeys[i]].focus();
              return;
            }
          }

          // Validate dates
          for (let i = 0; i < refsKeys.length; i++) {
            const [process, dateTimeKey] = refsKeys[i].split('^');
            const valueString = opsClockValuesRefs.current[refsKeys[i]].value;
            if (!dateTimeKey.endsWith('Date') || dateTimeKey.endsWith('DateTime')) continue;
            const [isValid, errorMessage] = isValidOpsClockDate(valueString, refsKeys[i]);
            if (!isValid) {
              e.preventDefault();
              opsClockValuesRefs.current[refsKeys[i]].focus();
              return;
            }
          }

          setParentAssumptionsData();
        }
      };

      container.addEventListener('focusin', handleFocusIn);
      container.addEventListener('focusout', handleFocusOut);

      return () => {
        container.removeEventListener('focusin', handleFocusIn);
        container.removeEventListener('focusout', handleFocusOut);
      };
    }, []);

    const setInputRef = (id, element) => {
      if (element !== null) {
        opsClockValuesRefs.current[id] = element;
      }
    };

    function reset() {
      processes.forEach((process) => {
        const opsClockProcessName = OPS_CLOCK_PROCESS_MAPPINGS[process][0];
        const opsClockDatum = shiftPlanOpsClockMap.find((datum) => datum.process === opsClockProcessName);
        if (opsClockDatum) {
          const startDateTime = opsClockDatum['startTime'];
          const startDate = chainWalk(() => startDateTime.split('T')[0], '');
          const startTime = chainWalk(() => startDateTime.split('T')[1], '');
          const endDateTime = opsClockDatum['endTime'];
          const endDate = chainWalk(() => endDateTime.split('T')[0], '');
          const endTime = chainWalk(() => endDateTime.split('T')[1], '');

          let key = [process, 'startDate'].join('^');
          if (key in opsClockValuesRefs.current) {
            opsClockValuesRefs.current[key].value = startDate;
          }
          key = [process, 'startTime'].join('^');
          if (key in opsClockValuesRefs.current) {
            opsClockValuesRefs.current[key].value = startTime;
          }
          key = [process, 'endDate'].join('^');
          if (key in opsClockValuesRefs.current) {
            opsClockValuesRefs.current[key].value = endDate;
          }
          key = [process, 'endTime'].join('^');
          if (key in opsClockValuesRefs.current) {
            opsClockValuesRefs.current[key].value = endTime;
          }
        }
      });
      setParentAssumptionsData();
    }

    function setParentAssumptionsData() {
      props.setAssumptions((prev) => {
        if ('opsclockInput' in prev) {
          const prevOpsClockMap = JSON.stringify(
            prev.opsclockInput?.opsClockMap?.[props.selectedCycle] || shiftPlanOpsClockMap
          );
          const updatedOpsClockMap = JSON.parse(prevOpsClockMap);

          updatedOpsClockMap.forEach((opsClockDatum) => {
            // Map the opsClockDatum.process to the OPS_CLOCK_PROCESS_MAPPING key.
            const processMapping = Object.entries(OPS_CLOCK_PROCESS_MAPPINGS).find(([_, processes]) =>
              processes.includes(opsClockDatum.process)
            );
            if (processMapping) {
              let dateKey = [processMapping[0], 'startDate'].join('^');
              let timeKey = [processMapping[0], 'startTime'].join('^');
              if (dateKey in opsClockValuesRefs.current && timeKey in opsClockValuesRefs.current) {
                const startDate = opsClockValuesRefs.current[dateKey].value;
                const startTime = opsClockValuesRefs.current[timeKey].value;
                const startDateTime = startDate + 'T' + startTime.slice(0, 5);
                opsClockDatum['startTime'] = startDateTime;
              }

              dateKey = [processMapping[0], 'endDate'].join('^');
              timeKey = [processMapping[0], 'endTime'].join('^');
              if (dateKey in opsClockValuesRefs.current && timeKey in opsClockValuesRefs.current) {
                const endDate = opsClockValuesRefs.current[dateKey].value;
                const endTime = opsClockValuesRefs.current[timeKey].value;
                const endDateTime = endDate + 'T' + endTime.slice(0, 5);
                opsClockDatum['endTime'] = endDateTime;
              }
            }
          });

          return {
            ...prev,
            opsclockInput: {
              ...prev.opsclockInput,
              opsClockMap: {
                ...prev.opsclockInput?.opsClockMap,
                [props.selectedCycle]: updatedOpsClockMap
              }
            }
          };
        } else {
          return prev;
        }
      });
    }

    // Validation helper variables
    const timeMask = new InputMask('##:##');
    const currentDateTime = formatDateForGraph(Date.now() / 1000, props.stationTimezone).replace(' ', 'T');
    const minOpsClockDateTime =
      shiftPlanOpsClockMap.length > 0
        ? formatDateForGraph(
            Math.min(...shiftPlanOpsClockMap.map((opsClockEntry) => new Date(opsClockEntry['startTime']).getTime())) /
              1000
          )
        : formatDateForGraph(Date.now() / 1000, props.stationTimezone);
    const maxOpsClockDateTime =
      shiftPlanOpsClockMap.length > 0
        ? formatDateForGraph(
            Math.max(...shiftPlanOpsClockMap.map((opsClockEntry) => new Date(opsClockEntry['endTime']).getTime())) /
              1000
          )
        : formatDateForGraph(Date.now() / 1000, props.stationTimezone);

    const isValidOpsClockDate = (dateString, dateFieldId) => {
      // Date string length
      if (dateString.length !== 10) return [false, 'invalid date.'];
      // Date string format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(dateString)) return [false, 'invalid date.'];
      // Month and Day values
      const [year, month, day] = dateString.split('-').map(Number);
      const februaryDays = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
      if (
        month < 1 ||
        month > 12 ||
        day < 1 ||
        ([4, 6, 9, 11].includes(month) && day > 30) ||
        ([1, 3, 5, 7, 8, 10, 12].includes(month) && day > 31) ||
        (month === 2 && day > februaryDays)
      ) {
        return [false, 'invalid date.'];
      }

      // Build full dateTime string for additional validations
      const [processName, dateType] = dateFieldId.split('^');
      const timeFieldId = [processName, dateType === 'endDate' ? 'endTime' : 'startTime'].join('^');
      const processDateTime = [dateString, opsClockValuesRefs.current[timeFieldId].value].join(' ');

      // Must be between min and max initial opsClockValues
      if (processDateTime < minOpsClockDateTime) return [false, 'before earliest initial opsClock start time.'];
      if (processDateTime > maxOpsClockDateTime) return [false, 'after latest initial opsClock end time.'];

      // Cannot be prior to current dateTime
      const currentDateTime = formatDateForGraph(Date.now() / 1000, props.stationTimezone);
      if (processDateTime < currentDateTime) return [false, 'before current station time.'];

      // Build full complement dateTime string for additional validations
      const complementDateFieldId = [processName, dateType === 'endDate' ? 'startDate' : 'endDate'].join('^');
      const complementTimeFieldId = [processName, dateType === 'endDate' ? 'startTime' : 'endTime'].join('^');
      let complementDateTime = processDateTime;
      if (complementDateFieldId in opsClockValuesRefs.current && complementTimeFieldId in opsClockValuesRefs.current) {
        complementDateTime = [
          opsClockValuesRefs.current[complementDateFieldId].value,
          opsClockValuesRefs.current[complementTimeFieldId].value
        ].join(' ');
      } else {
        const complementDateTimeFieldId = [processName, dateType === 'endDate' ? 'startDateTime' : 'endDateTime'].join(
          '^'
        );
        complementDateTime = opsClockValuesRefs.current[complementDateTimeFieldId].textContent;
      }

      // Must be according to its process complement datetime
      if (dateType === 'endDate') {
        if (processDateTime < complementDateTime) return [false, 'before process start time.'];
      } else {
        if (processDateTime > complementDateTime) return [false, 'after process end time.'];
      }

      return [true, ''];
    };

    const isValidOpsClockTime = (timeString, timeFieldId) => {
      // Time string length
      if (timeString.length !== 5) return [false, 'invalid time.'];
      // Time string format
      const timeRegex = /^\d{2}:\d{2}$/;
      if (!timeRegex.test(timeString)) return [false, 'invalid time.'];
      // Hours and Minutes values
      const [hours, minutes] = timeString.split(':').map(Number);
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return [false, 'invalid time.'];
      }

      // Build full dateTime string for additional validations
      const [processName, timeType] = timeFieldId.split('^');
      const dateFieldId = [processName, timeType === 'endTime' ? 'endDate' : 'startDate'].join('^');
      const processDateTime = [opsClockValuesRefs.current[dateFieldId].value, timeString].join(' ');

      // Must be between min and max initial opsClockValues
      if (processDateTime < minOpsClockDateTime) return [false, 'before earliest initial opsClock start time.'];
      if (processDateTime > maxOpsClockDateTime) return [false, 'after latest initial opsClock end time.'];

      // Cannot be prior to current dateTime
      const currentDateTime = formatDateForGraph(Date.now() / 1000, props.stationTimezone);
      if (processDateTime < currentDateTime) return [false, 'before current station time.'];

      // Build full complement dateTime string for additional validations
      const complementDateFieldId = [processName, timeType === 'endTime' ? 'startDate' : 'endDate'].join('^');
      const complementTimeFieldId = [processName, timeType === 'endTime' ? 'startTime' : 'endTime'].join('^');
      let complementDateTime = processDateTime;
      if (complementDateFieldId in opsClockValuesRefs.current && complementTimeFieldId in opsClockValuesRefs.current) {
        complementDateTime = [
          opsClockValuesRefs.current[complementDateFieldId].value,
          opsClockValuesRefs.current[complementTimeFieldId].value
        ].join(' ');
      } else {
        const complementDateTimeFieldId = [processName, timeType === 'endTime' ? 'startDateTime' : 'endDateTime'].join(
          '^'
        );
        complementDateTime = opsClockValuesRefs.current[complementDateTimeFieldId].textContent;
      }

      // Must be according to its process complement datetime
      if (timeType === 'endTime') {
        if (processDateTime < complementDateTime) return [false, 'before process start time.'];
      } else {
        if (processDateTime > complementDateTime) return [false, 'after process end time.'];
      }

      return [true, ''];
    };

    const headerRow = (
      <DataTableHead>
        <DataTableRow>
          <DataTableHeader>Process</DataTableHeader>
          <DataTableHeader>Start</DataTableHeader>
          <DataTableHeader>End</DataTableHeader>
        </DataTableRow>
      </DataTableHead>
    );

    const bodyRows = processes
      .filter((process) =>
        initialOpsClockMap.find((opsClockDatum) => opsClockDatum['process'] === OPS_CLOCK_PROCESS_MAPPINGS[process][0])
      ) // Filter out optional processes that don't exist for the selected site (like Sort_0/Twilight)
      .map((processDisplayName) => {
        const processOpsClockName = OPS_CLOCK_PROCESS_MAPPINGS[processDisplayName][0];
        const initialOpsClockData = initialOpsClockMap.find(
          (opsClockDatum) => opsClockDatum['process'] === processOpsClockName
        );
        const startTimeInput = initialOpsClockData['startTime'];
        const endTimeInput = initialOpsClockData['endTime'];

        const initialStartDate = chainWalk(() => startTimeInput.split('T')[0], '');
        const initialStartTime = chainWalk(() => startTimeInput.split('T')[1], '');
        const initialEndDate = chainWalk(() => endTimeInput.split('T')[0], '');
        const initialEndTime = chainWalk(() => endTimeInput.split('T')[1], '');

        const [startDate, setStartDate] = useState(initialStartDate);
        const [startTime, setStartTime] = useState(initialStartTime);
        const [endDate, setEndDate] = useState(initialEndDate);
        const [endTime, setEndTime] = useState(initialEndTime);
        const [startDateErrorMessage, setStartDateErrorMessage] = useState('');
        const [endDateErrorMessage, setEndDateErrorMessage] = useState('');
        const [startTimeErrorMessage, setStartTimeErrorMessage] = useState('');
        const [endTimeErrorMessage, setEndTimeErrorMessage] = useState('');

        const processStartDateId = [processDisplayName, 'startDate'].join('^');
        const processStartTimeId = [processDisplayName, 'startTime'].join('^');
        const processEndDateId = [processDisplayName, 'endDate'].join('^');
        const processEndTimeId = [processDisplayName, 'endTime'].join('^');
        const processStartDateTimeId = [processDisplayName, 'startDateTime'].join('^');
        const processEndDateTimeId = [processDisplayName, 'endDateTime'].join('^');

        const onDateBlur = (blurEvent) => {
          const targetId = blurEvent.target.id;
          const dateString = blurEvent.target.value;
          const [isValid, errorMessage] = isValidOpsClockDate(dateString, targetId);

          // Identify which date we are handling (start or end)
          const [_, whichDate] = targetId.split('^');

          if (!isValid) {
            blurEvent.preventDefault();
            blurEvent.target.value = whichDate === 'startDate' ? initialStartDate : initialEndDate;
            opsClockValuesRefs.current[targetId].focus();
          }
          // Set/Clear error message
          if (whichDate === 'startDate') {
            setStartDateErrorMessage(errorMessage);
          } else if (whichDate === 'endDate') {
            setEndDateErrorMessage(errorMessage);
          }
        };

        const onTimeBlur = (blurEvent) => {
          const targetId = blurEvent.target.id;
          const timeString = blurEvent.target.value;
          const [isValid, errorMessage] = isValidOpsClockTime(timeString, targetId);

          // Identify which time we are handling (start or end)
          const [_, whichTime] = targetId.split('^');

          if (!isValid) {
            blurEvent.preventDefault();
            blurEvent.target.value = whichTime === 'startTime' ? initialStartTime : initialEndTime;
            opsClockValuesRefs.current[targetId].focus();
          }
          // Set/Clear error message
          if (whichTime === 'startTime') {
            setStartTimeErrorMessage(errorMessage);
          } else if (whichTime === 'endTime') {
            setEndTimeErrorMessage(errorMessage);
          }
        };

        return (
          <DataTableRow highlightOnHover={true} alignmentVertical="bottom">
            <DataTableCell>{processDisplayName}</DataTableCell>
            <DataTableCell>
              {props.isEditMode && currentDateTime < startTimeInput ? (
                <Row spacing="100" alignmentVertical="bottom">
                  <DateInputField
                    id={processStartDateId}
                    disabled={isUseDefault}
                    ref={(el) => setInputRef(processStartDateId, el)}
                    size="small"
                    format="yyyy-MM-dd"
                    value={startDate}
                    errorMessage={startDateErrorMessage}
                    onChange={setStartDate}
                    onBlur={onDateBlur}
                  />
                  <Input
                    id={processStartTimeId}
                    disabled={isUseDefault}
                    ref={(el) => setInputRef(processStartTimeId, el)}
                    size="small"
                    mask={timeMask}
                    value={startTime}
                    errorMessage={startTimeErrorMessage}
                    onChange={setStartTime}
                    onBlur={onTimeBlur}
                  />
                </Row>
              ) : (
                <Text id={processStartDateTimeId} ref={(el) => setInputRef(processStartDateTimeId, el)}>
                  {startDate + ' ' + startTime}
                </Text>
              )}
            </DataTableCell>
            <DataTableCell>
              {props.isEditMode && currentDateTime < endTimeInput ? (
                <Row spacing="100" alignmentVertical="bottom">
                  <DateInputField
                    id={processEndDateId}
                    disabled={isUseDefault}
                    ref={(el) => setInputRef(processEndDateId, el)}
                    size="small"
                    format="yyyy-MM-dd"
                    value={endDate}
                    errorMessage={endDateErrorMessage}
                    onChange={setEndDate}
                    onBlur={onDateBlur}
                  />
                  <Input
                    id={processEndTimeId}
                    disabled={isUseDefault}
                    ref={(el) => setInputRef(processEndTimeId, el)}
                    size="small"
                    mask={timeMask}
                    value={endTime}
                    errorMessage={endTimeErrorMessage}
                    onChange={setEndTime}
                    onBlur={onTimeBlur}
                  />
                </Row>
              ) : (
                // <Text id={processEndDateTimeId}>{endDate + ' ' + endTime}</Text>
                <Text id={processEndDateTimeId} ref={(el) => setInputRef(processEndDateTimeId, el)}>
                  {endDate + ' ' + endTime}
                </Text>
              )}
            </DataTableCell>
          </DataTableRow>
        );
      });

    return (
      <Column ref={opsClockTableRef}>
        <Box type="fill" spacingInset="300">
          <Row>
            <Text type="h300">
              Ops Clock{' '}
              {chainWalk(() => props.shiftPlanDynamic['input']['opsclockInput']['overrideBasis']['reason'], false) &&
                '(Overridden)'}
            </Text>
            {props.isEditMode && (
              <Row>
                <Button
                  size="small"
                  type="secondary"
                  disabled={isUseDefault}
                  onClick={() => {
                    reset();
                  }}>
                  Reset
                </Button>
                <Toggle
                  checked={isUseDefault}
                  onChange={(checked) => {
                    setIsUseDefault(checked);

                    if (checked) {
                      props.setAssumptions((prev) => {
                        delete prev['opsclockInput'];
                        return prev;
                      });
                    } else {
                      props.setAssumptions((prev) => ({
                        ...prev,
                        opsclockInput: {
                          opsClockMap: props.shiftPlanDynamic['input']['opsclockInput']['opsClockMap'],
                          overrideBasis: {
                            reason: ASSUMPTION,
                            comment: ''
                          }
                        }
                      }));
                    }
                  }}>
                  Use default
                </Toggle>
              </Row>
            )}
          </Row>
        </Box>
        <DataTable headerRows={1}>
          {headerRow}
          <DataTableBody>{bodyRows}</DataTableBody>
        </DataTable>
      </Column>
    );
  };

  return (
    <Column width="100%" spacing="600">
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <PackageProfileTable
          assumptions={props.assumptions}
          setAssumptions={props.setAssumptions}
          shiftPlanDynamic={props.shiftPlanDynamic}
          selectedCycle={props.selectedCycle}
          isEditMode={props.isEditMode}
          stationLocalStablePlanTime={props.stationLocalStablePlanTime}
        />
      </div>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <OpsClockTable
          stationTimezone={props.stationTimezone}
          assumptions={props.assumptions}
          setAssumptions={props.setAssumptions}
          shiftPlanDynamic={props.shiftPlanDynamic}
          selectedCycle={props.selectedCycle}
          isEditMode={props.isEditMode}
        />
      </div>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <LaborAvailabilitiesTable
          assumptions={props.assumptions}
          setAssumptions={props.setAssumptions}
          shiftPlanDynamic={props.shiftPlanDynamic}
          isEditMode={props.isEditMode}
          stationLocalStablePlanTime={props.stationLocalStablePlanTime}
        />
      </div>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <HeadcountTable
          assumptions={props.assumptions}
          setAssumptions={props.setAssumptions}
          shiftPlanDynamic={props.shiftPlanDynamic}
          isEditMode={props.isEditMode}
          selectedCycle={props.selectedCycle}
          stationLocalStablePlanTime={props.stationLocalStablePlanTime}
        />
      </div>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <RatesTable
          assumptions={props.assumptions}
          setAssumptions={props.setAssumptions}
          shiftPlanDynamic={props.shiftPlanDynamic}
          isEditMode={props.isEditMode}
          selectedCycle={props.selectedCycle}
          stationLocalStablePlanTime={props.stationLocalStablePlanTime}
        />
      </div>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <MechanicalConstraintsTable
          assumptions={props.assumptions}
          setAssumptions={props.setAssumptions}
          shiftPlanDynamic={props.shiftPlanDynamic}
          isEditMode={props.isEditMode}
        />
      </div>
      <br />
    </Column>
  );
};
export default DynamicShiftPlanBetaTable;
